import * as React from 'react';
import { Activity, Check, Rocket, Shield, Target, Users } from 'lucide-react';

import { cn } from '@/lib/utils/cn';

/**
 * The engagement module cards: the new-engagement wizard's Modules step
 * and the Edit engagement details modal render the same list.
 *
 * Visual contract: the prototype's `modals-wizard.js → stepModules()` +
 * `wizard.css → .module-card`. Selected card: amber border + 1px amber
 * ring, amber-50 fill, amber check pip, amber-100 icon tile. Tokens only.
 *
 * `compact` is the edit modal's narrow column: one row per module (icon,
 * title, code, check pip) without the description, so all five fit
 * without the dialog growing past the viewport.
 *
 * Keys are the API's module names (`modules` on POST/PATCH
 * /api/v1/projects, `modules_enabled` on the engagement DTOs).
 */

export type EngagementModuleKey =
  | 'contract_compliance'
  | 'health_check'
  | 'go_live_readiness'
  | 'selection'
  | 'discovery';

export interface EngagementModuleDef {
  key: EngagementModuleKey;
  title: string;
  code: string;
  desc: string;
  Icon: typeof Shield;
}

/**
 * Why Selection cannot be unticked while Discovery is ticked (T-0171:
 * Discovery's requirement suggestions and sign-off live in Selection; the API
 * turns Selection on with Discovery and refuses to turn it off). Shown on
 * the locked Selection card by the wizard's Modules step and the Edit
 * engagement details modal alike.
 */
export const SELECTION_LOCK_REASON =
  'Stays on while Discovery is on: its requirements live in Selection.';

/**
 * The IV&V modules: the ones with a contract baseline to form. An
 * engagement with none of them on (Discovery and/or Selection only) has no
 * SOW to read, so the API refuses an AI base-formation run for it and the
 * Documents screen says so instead of offering one. The wizard applies the
 * same rule to its own module keys (`usesContractBaseline` in
 * `@/lib/wizard/new-engagement-state`); the reasoning is written there.
 */
export const BASELINE_MODULE_KEYS: ReadonlyArray<EngagementModuleKey> = [
  'contract_compliance',
  'health_check',
  'go_live_readiness',
];

/** Whether an engagement's `modules_enabled` includes an IV&V module. */
export function modulesUseContractBaseline(modulesEnabled: ReadonlyArray<string>): boolean {
  return BASELINE_MODULE_KEYS.some((key) => modulesEnabled.includes(key));
}

export const ENGAGEMENT_MODULES: ReadonlyArray<EngagementModuleDef> = [
  {
    key: 'contract_compliance',
    title: 'Contract Compliance',
    code: 'E06',
    desc: 'SOW + change orders, deliverable EDL, IV&V checkpoints, vendor scoring, deviation & pulse reports.',
    Icon: Shield,
  },
  {
    key: 'health_check',
    title: 'Health Check',
    code: 'E07',
    desc: 'Configurable domain assessment, register, three-layer budget, schedule, interviews, consolidated findings.',
    Icon: Activity,
  },
  {
    key: 'go_live_readiness',
    title: 'Go-Live Readiness',
    code: 'E08',
    desc: 'Weighted scorecard across testing, defects, data, cutover, readiness. AI scoring with manual override.',
    Icon: Rocket,
  },
  {
    key: 'selection',
    title: 'Selection',
    code: 'E11',
    desc: 'Client requirements from the firm library or written for this client, each with a criticality and a citation.',
    Icon: Target,
  },
  {
    key: 'discovery',
    title: 'Discovery',
    code: 'E12',
    desc: 'Questionnaires, interviews and client documents turned into findings, process maps and a signed-off requirements packet.',
    Icon: Users,
  },
];

export interface ModuleCardsProps {
  /** Which modules to show, in this order. */
  modules: ReadonlyArray<EngagementModuleKey>;
  selected: Readonly<Partial<Record<EngagementModuleKey, boolean>>>;
  onToggle: (key: EngagementModuleKey, next: boolean) => void;
  /** Modules that cannot be changed right now, with the one-line reason shown on the card. */
  locked?: Readonly<Partial<Record<EngagementModuleKey, string>>>;
  compact?: boolean;
  /** Disables every card (e.g. while a save is in flight). */
  disabled?: boolean;
}

export function ModuleCards({
  modules,
  selected,
  onToggle,
  locked,
  compact = false,
  disabled = false,
}: ModuleCardsProps) {
  const idPrefix = React.useId();
  const defs = modules
    .map((key) => ENGAGEMENT_MODULES.find((m) => m.key === key))
    .filter((m): m is EngagementModuleDef => m !== undefined);

  return (
    <div className={compact ? 'flex flex-col gap-2' : 'grid gap-3 sm:grid-cols-2 lg:grid-cols-3'}>
      {defs.map((m) => {
        const isOn = Boolean(selected[m.key]);
        const lockReason = locked?.[m.key];
        const titleId = `${idPrefix}-${m.key}-title`;
        const reasonId = `${idPrefix}-${m.key}-reason`;
        return (
          <button
            key={m.key}
            type="button"
            role="checkbox"
            aria-checked={isOn}
            aria-labelledby={titleId}
            aria-describedby={lockReason ? reasonId : undefined}
            disabled={disabled || Boolean(lockReason)}
            onClick={() => onToggle(m.key, !isOn)}
            className={cn(
              'relative rounded-card border bg-paper-0 text-left transition-shadow',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
              'disabled:cursor-not-allowed',
              compact
                ? 'flex w-full items-center gap-3 px-3 py-2.5'
                : 'flex min-h-[156px] flex-col gap-2 p-4',
              isOn
                ? 'border-accent-amber-600 bg-accent-amber-50 shadow-[0_0_0_1px_var(--accent-amber-600)]'
                : 'border-ink-100 hover:border-ink-300',
            )}
          >
            <span
              className={cn(
                'inline-flex h-[18px] w-[18px] flex-none items-center justify-center rounded border-[1.5px]',
                compact ? 'order-last' : 'absolute right-3 top-3',
                isOn
                  ? 'border-accent-amber-600 bg-accent-amber-600 text-paper-0'
                  : 'border-ink-200 bg-paper-0 text-transparent',
              )}
              aria-hidden="true"
            >
              <Check className="h-3 w-3" />
            </span>
            <span
              className={cn(
                'inline-flex h-8 w-8 flex-none items-center justify-center rounded-card',
                isOn ? 'bg-accent-amber-100 text-accent-amber-600' : 'bg-paper-100 text-ink-700',
              )}
              aria-hidden="true"
            >
              <m.Icon className="h-4.5 w-4.5" />
            </span>
            {compact ? (
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="flex items-baseline gap-2">
                  <span id={titleId} className="text-body-sm font-semibold text-ink-900">
                    {m.title}
                  </span>
                  <span className="font-mono text-meta text-ink-500">{m.code}</span>
                </span>
                {lockReason ? (
                  <span id={reasonId} className="text-meta text-ink-500">
                    {lockReason}
                  </span>
                ) : null}
              </span>
            ) : (
              <>
                <span id={titleId} className="text-body-sm font-semibold text-ink-900">
                  {m.title}
                </span>
                <span className="text-meta leading-[18px] text-ink-500">{m.desc}</span>
                {lockReason ? (
                  <span id={reasonId} className="text-meta text-ink-500">
                    {lockReason}
                  </span>
                ) : null}
                <span className="mt-auto font-mono text-meta text-ink-500">{m.code}</span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}
